import React, {
    useState, useEffect, useRef, useContext
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { hot } from 'react-hot-loader';
import { Button } from '@ferris/react';
import throttle from 'lodash.throttle';
import { TrackingContext } from '@vrbo/react-event-tracking';

import TranslationUtil from '../../utils/TranslationUtil';
import ExperimentUtil from '../../utils/ExperimentUtil';
import { howItWorksActive, isRebrandingActive } from '../../utils/ExperimentsUtil';
import Slider from '../Slider';
import { createImpressionEvent, createInteractionEvent } from '../../utils/EventProviders/EgClickstreamUtil';


export const HowItWorksSection = (props) => {
    const {
        isHowItWorks, howItWorksSlides, title, cta, isFrboFlow,
        isRebranded
    } = props;
    if (!isHowItWorks) return null;
    const { trigger } = useContext(TrackingContext);
    const [isHowItWorksViewed, setIsHowItWorksViewed] = useState(false);
    const HowItWorks = useRef();
    const isHowItWorksVisible = () => {
        /* istanbul ignore else */
        if (HowItWorks.current) {
            return (HowItWorks.current.getBoundingClientRect().top
            - (HowItWorks.current.offsetHeight + 150)) < 0;
        }
        return false;
    };

    const onScroll = () => {
        if (isHowItWorksVisible()) {
            setIsHowItWorksViewed(true);
        }
    };
    const scrollHandler = throttle(onScroll, 333);

    useEffect(() => {
        window.addEventListener('scroll', scrollHandler);
        return () => {
            window.removeEventListener('scroll', scrollHandler);
        };
    }, []);
    useEffect(() => {
        if (isHowItWorksViewed) {
            trigger('impression', createImpressionEvent('section.viewed', 'Property Onboarding', 'how-it-works'));
        }
    }, [isHowItWorksViewed]);

    const onClick = () => {
        trigger('interaction', createInteractionEvent('banner_cta.selected', 'Property Onboarding', 'how-it-works'));
    };

    return (
        <section
            id="How-it-works"
            className={cn('how-it-works')}
            ref={HowItWorks}
        >
            <div className={cn('hiw-header')}>
                <h2>
                    {title}
                </h2>
                <a href="#ulx-hero">
                    <Button
                        type={(isFrboFlow || isRebranded) ? 'utility' : 'primary'}
                        variant={(isFrboFlow || isRebranded) ? 'utility-standard' : 'primary-standard'}
                        onClick={onClick}
                    >
                        {cta}
                    </Button>
                </a>
            </div>
            <Slider
                addZero={isRebranded}
                details="how-it-works"
                slides={howItWorksSlides}
            />
        </section>
    );
};

HowItWorksSection.propTypes = {
    title: PropTypes.string.isRequired,
    cta: PropTypes.string.isRequired,
    isHowItWorks: PropTypes.bool.isRequired,
    howItWorksSlides: PropTypes.arrayOf(PropTypes.shape({
        headline: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        image: PropTypes.shape({
            url: PropTypes.string.isRequired,
            alt: PropTypes.string
        }).isRequired
    })).isRequired,
    isFrboFlow: PropTypes.bool.isRequired,
    isRebranded: PropTypes.bool.isRequired
};

/* istanbul ignore next */
export const mapStateToProps = (state) => {
    const { app } = state;
    const {
        features
    } = app;
    const { isFrboFlow } = features;
    const experimentsUtil = new ExperimentUtil(app.experiments);
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    // eslint-disable-next-line max-len
    const getTranslation = (translation, params = undefined) => translations.getTranslation(translation, params);
    const isHowItWorks = howItWorksActive(experimentsUtil);
    const howItWorksSlides = isFrboFlow ? [
        {
            headline: getTranslation('Vrbo.howItWorks.slide1.headline'),
            description: getTranslation('Vrbo.howItWorks.slide1.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/vr-how-to-list/01-VR_Create_account_v2.webp',
                alt: getTranslation('Vrbo.howItWorks.slide1.headline')
            }
        },
        {
            headline: getTranslation('Vrbo.howItWorks.slide2.headline'),
            description: getTranslation('Vrbo.howItWorks.slide2.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/vr-how-to-list/02-VR_Show%20off%20your%20space.webp',
                alt: getTranslation('Vrbo.howItWorks.slide2.headline')
            }
        },
        {
            headline: getTranslation('Vrbo.howItWorks.slide3.headline'),
            description: getTranslation('Vrbo.howItWorks.slide3.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/vr-how-to-list/03-VR_Host%20your%20way.webp',
                alt: getTranslation('Vrbo.howItWorks.slide3.headline')
            }
        },
        {
            headline: getTranslation('Vrbo.howItWorks.slide4.headline'),
            description: getTranslation('Vrbo.howItWorks.slide4.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/vr-how-to-list/04-VR_Get%20your%20listing%20live.webp',
                alt: getTranslation('Vrbo.howItWorks.slide4.headline')
            }
        },
        {
            headline: getTranslation('Vrbo.howItWorks.slide5.headline'),
            description: getTranslation('Vrbo.howItWorks.slide5.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/vr-how-to-list/05-VR_Manage%20your%20property.webp',
                alt: getTranslation('Vrbo.howItWorks.slide5.headline')
            }
        }
    ] : [
        {
            headline: getTranslation('CL.howItWorks.slide1.headline'),
            description: getTranslation('CL.howItWorks.slide1.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/01-Hotel_Tell%20us%20about%20your%20property.webp',
                alt: getTranslation('CL.howItWorks.slide1.headline')
            }
        },
        {
            headline: getTranslation('CL.howItWorks.slide2.headline'),
            description: getTranslation('CL.howItWorks.slide2.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/02-Hotel-Create%20your%20account.webp',
                alt: getTranslation('CL.howItWorks.slide2.headline')
            }
        },
        {
            headline: getTranslation('CL.howItWorks.slide3.headline'),
            description: getTranslation('CL.howItWorks.slide3.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/03-Hotel-Show%20off%20your%20rooms_V2.webp',
                alt: getTranslation('CL.howItWorks.slide3.headline')
            }
        },
        {
            headline: getTranslation('CL.howItWorks.slide4.headline'),
            description: getTranslation('CL.howItWorks.slide4.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/04-Hotel-Go%20live%20faster_V2.webp',
                alt: getTranslation('CL.howItWorks.slide4.headline')
            }
        },
        {
            headline: getTranslation('CL.howItWorks.slide5.headline'),
            description: getTranslation('CL.howItWorks.slide5.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/05-Hotel-Review%20and%20submit_V2.webp',
                alt: getTranslation('CL.howItWorks.slide5.headline')
            }
        },
        {
            headline: getTranslation('CL.howItWorks.slide6.headline'),
            description: getTranslation('CL.howItWorks.slide6.description'),
            image: {
                url: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/06-Hotel-Manage%20everything%20in%20one%20place.webp',
                alt: getTranslation('CL.howItWorks.slide6.headline')
            }
        }
    ];

    const isRebranded = isRebrandingActive(experimentsUtil);

    return {
        isRebranded,
        title: getTranslation(`${isFrboFlow ? 'Vrbo' : 'CL'}.howItWorks.title`),
        cta: getTranslation(`${isFrboFlow ? 'Vrbo' : 'CL'}.howItWorks.cta`),
        isHowItWorks,
        howItWorksSlides,
        isFrboFlow
    };
};

export default hot(module)(connect(mapStateToProps)(HowItWorksSection));
